import { ChevronLeft, ChevronRight, CircleCheck, CircleX, Hourglass, ListChecks, LoaderCircle, RefreshCw } from 'lucide-react'
import type { ReactNode } from 'react'

import { Button, StatusMessage } from '@krp/ui-kit'

import { DATA_KIND_LABELS, JOB_ITEM_ACTION_LABELS, JOB_ITEM_LABELS, formatMoment, serverErrorShown } from '../model/labels'
import type { JobItemState, JournalEntry, ShownError } from '../model/types'
import { ErrorBlock } from './parts'
import './sync.css'

export interface JournalVm {
  status: 'loading' | 'failed' | 'ready'
  loadError: ShownError | null
  items: JournalEntry[]
  nameOf: (resourceId: string) => string
  page: number
  pages: number
  refreshing: boolean
}

export interface JournalActions {
  pageChanged: (page: number) => void
  openJob: (jobId: string) => void
  reload: () => void
}

const STATE_ICONS: Readonly<Record<JobItemState, ReactNode>> = {
  pending: <Hourglass size={14} aria-hidden="true" />,
  running: <LoaderCircle size={14} aria-hidden="true" />,
  succeeded: <CircleCheck size={14} aria-hidden="true" />,
  failed: <CircleX size={14} aria-hidden="true" />,
}

function whereText(entry: JournalEntry, nameOf: (resourceId: string) => string): string {
  const target = nameOf(entry.targetResourceId)
  return entry.sourceResourceId === null ? `в «${target}»` : `из «${nameOf(entry.sourceResourceId)}» в «${target}»`
}

/** Журнал синхронизации (требования 20.15): кто, что, куда и когда применил и чем кончилось — по строке на действие. */
export function JournalScreen({ vm, actions }: { vm: JournalVm; actions: JournalActions }) {
  return (
    <section aria-labelledby="sync-journal-title" className="sync">
      <header>
        <h2 id="sync-journal-title">Журнал синхронизации</h2>
        <p className="sync__lead">Всё, что люди применили в синхронизации: кто, что, откуда и куда, и чем кончилось.</p>
      </header>

      {vm.status === 'loading' && <StatusMessage kind="loading" title="Загружаем журнал…" />}
      {vm.status === 'failed' && vm.loadError !== null && (
        <ErrorBlock
          error={vm.loadError}
          action={
            <Button variant="secondary" icon={<RefreshCw size={16} aria-hidden="true" />} onClick={actions.reload} actionId="sync.journal.reload">
              Повторить
            </Button>
          }
        />
      )}
      {vm.status === 'ready' &&
        (vm.items.length === 0 ? (
          <StatusMessage kind="empty" title="В журнале пока ничего нет." />
        ) : (
          <ul className="sync-list" aria-label="Записи журнала">
            {vm.items.map((entry) => (
              <li key={entry.itemId} className="sync__panel">
                <div className="sync__row">
                  <span className="sync-badge" data-state={entry.state}>
                    {STATE_ICONS[entry.state]}
                    {JOB_ITEM_LABELS[entry.state]}
                  </span>
                  <strong>{entry.title}</strong>
                </div>
                <p className="sync__note">
                  {JOB_ITEM_ACTION_LABELS[entry.action]} · {DATA_KIND_LABELS[entry.dataKind]} · {whereText(entry, vm.nameOf)}
                </p>
                <div className="sync__row">
                  <span className="sync__note">
                    {entry.actorName ?? entry.actor}, {formatMoment(entry.at)}
                  </span>
                  <Button variant="ghost" icon={<ListChecks size={16} aria-hidden="true" />} onClick={() => actions.openJob(entry.jobId)} actionId="sync.journal.open-job">
                    Вся задача
                  </Button>
                </div>
                {entry.state === 'failed' && entry.message !== null && <ErrorBlock error={serverErrorShown(entry.title, entry.message, null, null)} />}
              </li>
            ))}
          </ul>
        ))}
      {vm.pages > 1 && (
        <nav className="sync__row" aria-label="Страницы журнала">
          <Button
            variant="secondary"
            icon={<ChevronLeft size={16} aria-hidden="true" />}
            disabled={vm.page <= 1}
            disabledReason={vm.page <= 1 ? 'Это первая страница.' : null}
            onClick={() => actions.pageChanged(vm.page - 1)}
            actionId="sync.journal.page"
          >
            Новее
          </Button>
          <span aria-current="page">
            Страница {vm.page} из {vm.pages}
          </span>
          <Button
            variant="secondary"
            icon={<ChevronRight size={16} aria-hidden="true" />}
            disabled={vm.page >= vm.pages}
            disabledReason={vm.page >= vm.pages ? 'Это последняя страница.' : null}
            onClick={() => actions.pageChanged(vm.page + 1)}
            actionId="sync.journal.page"
          >
            Старее
          </Button>
        </nav>
      )}
    </section>
  )
}
